import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { PasswordResetResponse } from 'types/auth';
import { useFetch } from 'app/utils/hooks';
import { fieldValidationMessages } from 'app/constants';
import { ApiErrorCodeMessages, ApiErrorCodeTypes } from 'types/enums';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import { JSONSchema7 } from 'json-schema';
import { dynamicYupResolver } from 'app/utils/utils';
import { Box, Button, Text } from '@chakra-ui/react';
import { ChakraAuthContentWrapper } from 'app/components/Core';
import { InputField } from 'app/components/Core/ChakraInputField/InputField';
import { LogInLink } from 'app/components/PasswordReset/LogInLink';
import { useQueryParams } from 'app/components/SignUp/SignUpForm/hooks';

interface Form {
  email: string;
}

const errorMessages = {
  errors: {
    email: {
      required: fieldValidationMessages.REQUIRED_EMAIL,
    },
  },
};

interface PasswordResetFormProps {
  onReset: (confirmLink: string, email: string) => void;
  passwordResetLink: string;
  emailValidation: JSONSchema7;
}

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  onReset,
  passwordResetLink,
  emailValidation,
}) => {
  const queryParams = useQueryParams();
  const defaultValues: Form = {
    email: queryParams.get('emailAddress') || '',
  };

  const { callAPI } = useFetch<Form, PasswordResetResponse>();

  const yupValidationSchema = useMemo(
    () =>
      emailValidation &&
      transformSchemaToYup(emailValidation, defaultValues, errorMessages),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emailValidation]
  );

  const {
    handleSubmit,
    register,
    setError,
    clearErrors,
    formState: { isValid, isSubmitting, errors },
  } = useForm<Form>({
    defaultValues,
    mode: 'onChange',
    context: {
      yupValidationSchema,
    },
    resolver: dynamicYupResolver,
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      clearErrors();
      const { response } = await callAPI(passwordResetLink, data);
      const {
        email,
        _links: { confirmForgotPassword },
      } = response.code.item;
      onReset(confirmForgotPassword.href, email);
    } catch (e: any) {
      const errorMessage = e.response.data.error.statusReason;
      setError('email', {
        message: ApiErrorCodeMessages[errorMessage as ApiErrorCodeTypes],
      });
    }
  });

  return (
    <ChakraAuthContentWrapper pt="56px">
      <Box as="form" onSubmit={onSubmit} textAlign="left">
        <Box mb={12}>
          <Text fontSize="xs" color="textSubdued">
            Reset your password
          </Text>
          <Text
            w="full"
            my={2}
            fontSize="lg"
            fontWeight={500}
            data-testid="primary-message"
          >
            You will receive an email with instructions if the email address
            provided is registered.
          </Text>
        </Box>
        <InputField
          {...register('email')}
          label="Email"
          isInvalid={Boolean(errors.email)}
          errorMessage={errors.email?.message}
          data-testid="Email"
        />
        <Button
          mt="22px"
          mb={12}
          w="full"
          type="submit"
          isDisabled={!isValid || isSubmitting}
          data-testid="PasswordResetForm-reset-btn"
        >
          Reset
        </Button>

        <LogInLink />
      </Box>
    </ChakraAuthContentWrapper>
  );
};
